import { CommonModule } from "@angular/common";
import { Component, inject } from "@angular/core";
import { Router, RouterModule } from "@angular/router";
import { TranslocoDirective } from "@jsverse/transloco";
import { TooltipModule } from "primeng/tooltip";
import { LANGUAGE_METADATA_LOCALIZED } from "../../../../domain/languages";
import { LocalizationService } from "../../../../services/localization.service";
import { BrandLogoComponent } from "../../../branding/brand-logo.component";
import { BrandService } from "../../../branding/brand.service";

@Component({
  selector: "app-unauthenticated-layout",
  standalone: true,
  imports: [CommonModule, RouterModule, TooltipModule, TranslocoDirective, BrandLogoComponent],
  template: `
    <ng-container *transloco="let t">
      <div class="flex flex-col lg:flex-row min-h-screen">
        <!-- Left Side -->
        <div
          class="flex w-full lg:w-1/2 flex-col justify-center px-6 px-8 md:px-20 lg:px-16 py-12 bg-surface-50 lg:items-start lg:text-left"
        >
          <!-- Logo -->
          <div class="mb-10">
            <app-brand-logo variant="horizontal" className="max-w-xs" />
          </div>

          <div class="flex flex-col gap-4">
            <h1 class="text-3xl md:text-4xl font-bold text-surface-900">{{ t("app.sign-in.header") }}</h1>

            <p class="text-base md:text-lg text-surface-700">
              {{ t("app.sign-in.subtitle") }}
            </p>

            <p class="text-sm text-surface-500">
              {{ t("app.sign-in.hint") }}
              <a [href]="'mailto:' + brandService.brand().supportEmail" class="text-primary underline">
                {{ brandService.brand().supportEmail }}
              </a>
            </p>
          </div>

          <!-- Language Selector -->
          <div class="mt-8 flex gap-x-4">
            <!-- Finnish -->
            <button
              (click)="localizationService.changeLanguage('fi', { skipRemoteUpdate: true })"
              [attr.aria-label]="languageMetadataLocalized.fi.name"
              class="cursor-pointer"
              [pTooltip]="languageMetadataLocalized.fi.name"
            >
              <img src="flags/fi.svg" [attr.alt]="languageMetadataLocalized.fi.name" class="w-6 h-4 rounded shadow" />
            </button>

            <!-- Swedish -->
            <button
              (click)="localizationService.changeLanguage('sv', { skipRemoteUpdate: true })"
              [attr.aria-label]="languageMetadataLocalized.sv.name"
              class="cursor-pointer"
              [pTooltip]="languageMetadataLocalized.sv.name"
            >
              <img src="flags/sv.svg" [attr.alt]="languageMetadataLocalized.sv.name" class="w-6 h-4 rounded shadow" />
            </button>

            <!-- English -->
            <button
              (click)="localizationService.changeLanguage('en', { skipRemoteUpdate: true })"
              [attr.aria-label]="languageMetadataLocalized.en.name"
              class="cursor-pointer"
              [pTooltip]="languageMetadataLocalized.en.name"
            >
              <img src="flags/en.svg" [attr.alt]="languageMetadataLocalized.en.name" class="w-6 h-4 rounded shadow" />
            </button>

            <!-- Norwegian -->
            <button
              (click)="localizationService.changeLanguage('no', { skipRemoteUpdate: true })"
              [attr.aria-label]="languageMetadataLocalized.no.name"
              class="cursor-pointer"
              [pTooltip]="languageMetadataLocalized.no.name"
            >
              <img src="flags/no.svg" [attr.alt]="languageMetadataLocalized.no.name" class="w-6 h-4 rounded shadow" />
            </button>
          </div>
        </div>

        <!-- Vertical Divider (hidden on small screens) -->
        <div class="hidden lg:block w-px bg-surface-200"></div>

        <!-- Right Side -->
        <div class="flex w-full lg:w-1/2 items-center justify-center px-6 md:px-12 lg:px-12 py-12">
          <router-outlet></router-outlet>
        </div>
      </div>
    </ng-container>
  `,
})
export class AppUnauthenticatedLayoutComponent {
  public readonly languageMetadataLocalized = LANGUAGE_METADATA_LOCALIZED;
  public readonly localizationService = inject(LocalizationService);
  public readonly router = inject(Router);
  public readonly brandService = inject(BrandService);
}
